import { get, has, includes, isEqual } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component, useContext } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, withRouter } from 'react-router-dom';
import { SubmissionError } from 'redux-form';
import PageContent from '../../../shared/components/PageContent';
import CompanyBrandForm from '../forms/Admin/CompanyForm/CompanyBrandForm';
import CompanyContentForm from '../forms/Admin/CompanyForm/CompanyContentForm';
import CompanyCustomLayoutsForm from '../forms/Admin/CompanyForm/CompanyCustomLayoutsForm';
import CompanyEmployeesForm from '../forms/Admin/CompanyForm/CompanyEmployees/CompanyEmployeesForm';
import CompanyFeaturesForm from '../forms/Admin/CompanyForm/CompanyFeaturesForm';
import CompanyIntegrationsForm from '../forms/Admin/CompanyForm/CompanyIntegrationsForm';
import CompanyLicensesForm from '../forms/Admin/CompanyForm/CompanyLicensesForm';
import CompanyManageTeamsForm from '../forms/Admin/CompanyForm/CompanyManageTeamsForm';
import CompanyNotificationForm from '../forms/Admin/CompanyForm/CompanyNotificationForm';
import CompanySettingsForm from '../forms/Admin/CompanyForm/CompanySettingsForm';
import CompanyReporting from '../forms/Admin/CompanyForm/CompanyReporting';
import CompanyAdminResources from '../forms/Admin/CompanyForm/CompanyAdminResources';

import { RuntimeContext } from '../../../../common/RuntimeContext';
import { getCompany, getManyTeamsAdmin, postCompany, putCompany, sendEmployeeNudges, setUnsavedChanges } from '../../../shared/actions';
import MenuTabs from '../../../shared/components/MenuTabs';
import ConfirmationModal from '../../../shared/components/modals/ConfirmationModal';
import Page from '../../../shared/components/Page';
import PageHeader from '../../../shared/components/PageHeader';
import VerbText from '../../../shared/components/VerbText';
import { FeatureFlagContext } from '../../../../common/FeatureFlagContext';
import ValidationModal from '../../../shared/components/formComponents/ValidationModal';

class NewAddOrEditCompany extends Component {
    static propTypes = {
        isVerbAdmin: PropTypes.bool,
        isAccountOwner: PropTypes.bool,
        isInstructionalAdmin: PropTypes.bool,
        sendEmployeeNudges: PropTypes.func,
        putCompany: PropTypes.func,
        getManyTeamsAdmin: PropTypes.func,
        postCompany: PropTypes.func,
        getCompany: PropTypes.func,
        history: PropTypes.object,
        currentCompany: PropTypes.object,
        loggedInUser: PropTypes.object,
        match: PropTypes.object
    };

    static contextType = RuntimeContext;

    constructor(props) {
        super(props);
        this.state = {
            activeMenuTab: 'Employees',
            hasUnsavedChanges: false,
            currentTeam: null,
            warnings: [],
            showSuccessBannerTeam: false,
            successBannerTeamName: '',
            checkTeamsPageFlag: false,
            isTeamsModalOpen: false,
            confirmationModal: false,
            confirmModalValue: null
        };
    }

    componentDidMount() {
        const { match, loggedInUser } = this.props;

        const companyId = match && match.params && match.params.id;

        if (companyId) {
            return this._getCompanyData(this.props.match.params.id);
        }
        return null;
    }

    componentDidUpdate(prevProps) {
        if (prevProps.match.params.id !== this.props.match.params.id) {
            return this._getCompanyData(this.props.match.params.id);
        }

        if (prevProps.match.params.section && prevProps.match.params.section !== this.props.match.params.section) {
            return this.toggleShowUnsavedChangesPrompt(false);
        }

        if (prevProps.location.pathname !== this.props.location.pathname) {
            this.checkIfTeamsPage(this.props.location.pathname);
        }

        if (this.state.showSuccessBannerTeam) {
            const teamsRegex = /^\/admin\/editcompany\/[^\/]+\/teams(\/[^\/]+)?$/;
            const isCurrentPathsTeams = teamsRegex.test(this.props.location.pathname);

            if (!isCurrentPathsTeams) {
                this.setState({
                    showSuccessBannerTeam: false,
                    successBannerTeamName: ''
                })
            }
        }

        return null;
    }

    checkIfTeamsPage = (path = '') => {
        const teamsRegex = /^\/admin\/editcompany\/[^\/]+\/teams$/;
        if (teamsRegex.test(path)) {
            this.setState({ checkTeamsPageFlag: true });
        } else {
            this.setState({ checkTeamsPageFlag: false });
        }
    };

    handleSuccessBannerTeamName = (teamName, bannerState = false) => {
        this.setState({
            successBannerTeamName: teamName || '',
            showSuccessBannerTeam: bannerState
        })
    }

    _getCompanyData = (companyId) => {
        const { getCompany, getManyTeamsAdmin } = this.props;

        return getCompany({ companyId, pathsContext: this.context.pathsWeb }).then(() =>
            getManyTeamsAdmin({
                parentId: companyId,
                getRegistered: false,
                getAllMembers: true,
                pathsContext: this.context.pathsWeb
            })
        );
    };

    handleSubmit = (values) => {
        const {
            currentCompany,
            putCompany,
            postCompany,
            match: { path }
        } = this.props;
        const isNew = includes(path, 'new');
        const newValues = {
            ...values
        };

        // add email notifications to active if company is new
        if (isNew) {
            // Company Notifications Default
            newValues.emailEnabled = true;
            newValues.emailConfigs = {
                peerRecommendation: true,
                teamLeadRecommendation: true,
                teamLeadAnnoucement: true,
                teamFeedPost: false,
                teamActivity: true,
                newContent: true,
                collectionReminder: true,
                discover: true,
                dueDateReminders: true
            };
            newValues.slackToken = null;
            newValues.slackNotifications = false;

            // Company Features Default
            newValues.hasCustomDimensions = false;
            newValues.SSOSecondaryText = '';
            newValues.isContentTabEnabled = true;
            newValues.isCompanyReportViewerCSVDownloadEnabled = false;
            newValues.socialAuthentication = false;
            newValues.teamsLabel = 'teams';
            newValues.frameworkId = '';
            newValues.frameworkOverviewAsset = {};
            newValues.customBranding = true;
            newValues.pathwaysEnabled = true;
            newValues.trackLicenses = [];
            newValues.showWelcomeFlow = false;
            newValues.hideWelcomeBanner = true;
        }

        if (newValues.companyLicenses) {
            const formattedCompanyLicenses = newValues.companyLicenses.map(({ value }) => value);
            newValues.companyLicenses = formattedCompanyLicenses;
        }

        // Only sets email domains if they exist in passed values as string and are populated
        if (typeof values.emailDomains === 'string') {
            newValues.emailDomains = values.emailDomains
                .split(',')
                .map((domain) => domain.trim())
                .filter((formattedDomain) => !!formattedDomain);
        }

        if (typeof values.emailDomainsBlacklist === 'string') {
            newValues.emailDomainsBlacklist = values.emailDomainsBlacklist
                .split(',')
                .map((domain) => domain.trim())
                .filter((formattedDomain) => !!formattedDomain);
        }

        if (typeof values.companyNames === 'string') {
            newValues.companyNames = [
                ...new Set(
                    values.companyNames
                        .split(',')
                        .map((domain) => domain.replace(/ /g, ''))
                        .filter((formattedCompany) => !!formattedCompany)
                )
            ];
        }

        if (
            values.customHome &&
            values.customHome.categories &&
            values.customHome.categories.length &&
            values.customHome.categories.length < 3
        ) {
            throw new SubmissionError({ _error: 'You need at least three rows to save this custom home page.' });
        }

        // Use membersIds from state provided by CompanyForm
        const newCompany = {
            ...currentCompany,
            ...newValues
        };

        let submitPromise;
        if (!has(newCompany, '_id')) {
            submitPromise = postCompany({
                body: newCompany,
                pathsContext: this.context.pathsWeb
            });
        } else if (!isEqual(newCompany, currentCompany)) {
            submitPromise = putCompany({
                companyId: newCompany._id,
                body: newCompany,
                pathsContext: this.context.pathsWeb
            });
        } else {
            submitPromise = Promise.resolve();
        }
        return submitPromise
            .then((updatedCompany) => {
                if (this.state.hasUnsavedChanges) {
                    this.setState({ hasUnsavedChanges: false });
                }
                if (isNew) {
                    this.props.history.push(`/admin/editcompany/${updatedCompany._id}`);
                }
            })
            .catch((err) => {
                const errorCode = get(err, 'body.errorCode');
                if (errorCode === 400.3) {
                    throw new SubmissionError({ _error: 'Company permalink already in use. Please choose another.' });
                }
                throw new SubmissionError({ _error: 'Submission failed' });
            });
    };

    handleCancel = () => {
        this.props.history.push('/admin/administration');
    };

    _getCompanyTabPath = ({ tabName, isNew }) => {
        const { match } = this.props;
        if (isNew) {
            return `/newcompany/${tabName}`;
        }
        return `/admin/editcompany/${match.params.id}/${tabName}`;
    };

    // eslint-disable-next-line no-alert
    confirm = (message) => window.confirm(message || 'You have unsaved changes, are you sure you want to switch tabs?');

    showUnsavedChangesPrompt = () => {
        const confirmation = this.confirm(
            'You have unsaved changes, are you sure you want to continue without saving changes?'
        );

        return !!confirmation;
    };

    toggleShowUnsavedChangesPrompt = (changeToState, callback) => {
        this.setState({
            hasUnsavedChanges: changeToState,
        }, callback);
    };

    addWarning = (tabId, sectionWarnings) => {
        this.setState((prevState) => {
            const { warnings } = prevState;
            warnings[tabId] = sectionWarnings;
            return {
                warnings
            };
        });
    };

    toggleConfirmationModal = (info) => {
        this.setState({
            confirmationModal: info
        });
    };

    toggleTeamConfirmationModal = value => {
        this.setState({
            hasUnsavedChanges: value,
            isTeamsModalOpen: value
        });
    }

    updateMenuTab = (tabId) => {
        const { warnings } = this.state;
        this.setState({
            warnings: {
                ...warnings,
                [tabId]: []
            }
        });
        return true;
    };

    teamUnsavedConfimationModal = () => {
        this.setState({
            confirmationModal: true
        });
    }

    confirmActionFunction = () => {
        this.setState({
            confirmationModal: false,
            confirmModalValue: true
        });

        this.props.setUnsavedChanges(false);
    }

    confirmCancelFunction = () => {
        this.setState({
            confirmationModal: false,
            confirmModalValue: false
        });
    }

    resetModalValues = () => {
        this.setState({
            isTeamsModalOpen: false,
            confirmationModal: false,
            confirmModalValue: null
        })
    }

    render() {
        const {
            currentCompany = {},
            match: {
                path,
                params: { section = '' }
            },
            history,
            match,
            isVerbAdmin,
            isAccountOwner,
            isInstructionalAdmin,
            loggedInUser,
            teamsUnsavedFlag,
            setUnsavedChanges
        } = this.props;
        const { hasUnsavedChanges, currentTeam, warnings, confirmationModal, showSuccessBannerTeam, successBannerTeamName, checkTeamsPageFlag, isTeamsModalOpen, confirmModalValue } = this.state;
        const isNew = includes(path, 'new');

        const subtitleWithDiscoverLink = <h4 className={'pageHeaderSubtitle'}>Use these settings to add custom rows in the <a target='_blank' href='/paths/discover'>Paths Discover page</a> for your organization.</h4>;
        const components = {
            content: { component: CompanyContentForm, title: `Paths Home`, subtitle: 'Use these settings to add assigned and recommended learning on the Paths home page for your organization.' },
            // layouts: { component: CompanyCustomLayoutsForm, title: `Customize Discover`, subtitle: 'Use these settings to add custom rows in the Paths Discover page for your organization.' },
            layouts: { component: CompanyCustomLayoutsForm, title: `Customize Discover`, subtitleWithLink: subtitleWithDiscoverLink },
            teams: {
                component: CompanyManageTeamsForm,
                title: `Teams${currentTeam ? `: ${currentTeam}` : ''}`
            },
            reporting: { component: CompanyReporting, title: `Reporting` },
            adminResources: { component: CompanyAdminResources, title: `Admin Resources` },
        };

        const activeMenuTabTitle = get(components[section], 'title', '');
        const title = isNew ? 'Create New Company' : `${activeMenuTabTitle}`;
        const teamsTabLabelDefaultMessage = currentCompany.teamsLabel
            ? currentCompany.teamsLabel.replace(
                  /\w\S*/g,
                  (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
              )
            : 'Teams';

        const subtitle = get(components[section], 'subtitle', '') || '';
        const subtitleWithLink = get(components[section], 'subtitleWithLink', '') || '';

        const formattedTeamsTitle = (
            <VerbText
                messageId={`${(currentCompany.teamsLabel || 'teams').toLocaleUpperCase()}_PL_TITLE`}
                defaultMessage={teamsTabLabelDefaultMessage}
            />
        );

        let isContentTabHidden = true;
        if (currentCompany.isContentTabEnabled) {
            isContentTabHidden = false;
        } else if (isVerbAdmin || isInstructionalAdmin || isAccountOwner) {
            isContentTabHidden = false;
        }

        if (!section) {
            return <Redirect to={`${match.url}/settings`} />;
        }
        const _menuTabs = (featureFlagData) =>{
            return  [
                {
                    name: 'Admin Resources',
                    icon: 'fa-info-circle',
                    tabId: 'adminResources',
                    title: 'adminResources',
                    path: this._getCompanyTabPath({ tabName: 'adminResources', isNew }),
                    isHidden: !featureFlagData.Paths_AdminResources
                },
                {
                    name: 'Paths Home',
                    icon: 'fa-book',
                    tabId: 'content',
                    title: 'Company Content',
                    path: this._getCompanyTabPath({ tabName: 'content', isNew }),
                    isHidden: isContentTabHidden
                },
                {
                    name: 'Customize Discover',
                    icon: 'fa-indent',
                    tabId: 'layouts',
                    title: 'Customize Discover',
                    path: this._getCompanyTabPath({ tabName: 'layouts', isNew }),
                    isHidden: (!isAccountOwner && !isVerbAdmin) || isNew || isInstructionalAdmin
                },
                {
                    name: formattedTeamsTitle,
                    icon: 'fa-users',
                    tabId: 'teams',
                    title: formattedTeamsTitle,
                    path: this._getCompanyTabPath({ tabName: 'teams', isNew }),
                    isHidden: (!isAccountOwner && !isVerbAdmin && !isInstructionalAdmin) || isNew
                },
                {
                    name: 'Reporting',
                    icon: 'fa-chart-bar',
                    tabId: 'reporting',
                    title: 'Reporting',
                    path: this._getCompanyTabPath({ tabName: 'reporting', isNew }),
                    isHidden: !featureFlagData.Paths_Reporting
                },
                // {
                //     name: 'Brand',
                //     icon: 'fa-heart',
                //     tabId: 'brand',
                //     title: 'Company Branding',
                //     path: this._getCompanyTabPath({ tabName: 'brand', isNew }),
                //     isHidden: isNew || !isVerbAdmin
                // },
                // {
                //     name: 'Notifications',
                //     icon: 'fa-bell',
                //     tabId: 'notifications',
                //     title: 'Notifications',
                //     path: this._getCompanyTabPath({ tabName: 'notifications', isNew }),
                //     isHidden: (!isAccountOwner && !isVerbAdmin) || isNew || isInstructionalAdmin
                // },
                // {
                //     name: 'Integrations',
                //     icon: 'fa-random',
                //     tabId: 'integrations',
                //     title: 'Integrations',
                //     path: this._getCompanyTabPath({ tabName: 'integrations', isNew }),
                //     isHidden: isNew || !isVerbAdmin
                // }
            ];
        }
        const routes = (featureFlagData)=>{
            const menuTabs = _menuTabs(featureFlagData);
            return menuTabs.map((tab) => {
                if (!tab.isHidden) {
                    return (
                        <Route
                            key={tab.path}
                            path={tab.path}
                            render={() => {
                                const ActiveMenuTabComponent = components[tab.tabId].component;
                                const tabWarnings = warnings[tab.tabId] || [];
                                return (
                                    <ActiveMenuTabComponent
                                        history={history}
                                        company={currentCompany}
                                        onSubmit={this.handleSubmit}
                                        onCancel={this.handleCancel}
                                        isVerbAdmin={isVerbAdmin}
                                        isAccountOwner={isAccountOwner}
                                        isInstructionalAdmin={isInstructionalAdmin}
                                        sendEmailReminders={this.sendEmailReminders}
                                        loggedInUser={loggedInUser}
                                        match={this.props.match}
                                        isNew={isNew}
                                        toggleShowUnsavedChangesPrompt={this.toggleShowUnsavedChangesPrompt}
                                        updateCurrentTeam={(teamName) => this.setState({ currentTeam: teamName })}
                                        currentTeamName={currentTeam}
                                        addWarning={this.addWarning}
                                        tabId={tab.tabId}
                                        tabWarnings={tabWarnings}
                                        toggleConfirmationModal={this.toggleConfirmationModal}
                                        featureFlagData={featureFlagData}
                                        handleSuccessBannerTeamName={this.handleSuccessBannerTeamName}
                                        toggleTeamConfirmationModal={this.toggleTeamConfirmationModal}
                                        showSuccessBannerTeam={showSuccessBannerTeam}
                                        teamUnsavedConfimationModal={this.teamUnsavedConfimationModal}
                                        teamsUnsavedFlag={teamsUnsavedFlag}
                                        setUnsavedChanges={setUnsavedChanges}
                                    />
                                );
                            }}
                        />
                    );
                }
                return null;
            });
        }
        const renderedSideNavItems = (featureFlagData) => (
            <MenuTabs
                menuItems={_menuTabs(featureFlagData)}
                initialTab={section}
                menuTabClick={(hasUnsavedChanges && isTeamsModalOpen ) ? this.teamUnsavedConfimationModal : (hasUnsavedChanges) ? this.showUnsavedChangesPrompt : this.updateMenuTab}
                confirmModalValue={confirmModalValue}
                history={this.props.history}
                resetModalValues={this.resetModalValues}
            />
        );

        const sectionWarnings = warnings[section] || [];

        const messageTextProp = {
          messageId: 'Confimation Modal for Unsaved Changes',
          defaultMessage: 'You have unsaved changes that will be discarded.',
        }

        const confimationTitle = 'Leave without saving?';

        const cancelButtonProp = {
            textId: 'Cancel btn',
            defaultMessage: 'Cancel'
        }

        const actionButtonProp = {
            textId: 'Confirm btn',
            defaultMessage: 'Leave without saving'
        }

        return (
            <FeatureFlagContext.Consumer>
                {
                    featureFlagData =>
                        <Page className={''}>
                            <div className={'col-md-2 verticalSubMenuList'}>{renderedSideNavItems(featureFlagData)}</div>
                            <div className={'new-add-or-edit-company-container col-sm-12 col-md-10'}>
                                <div className={'col-md-12 new-add-or-edit-company container float-helper'}>
                                    {showSuccessBannerTeam && checkTeamsPageFlag && successBannerTeamName && <div className='team-success-message-container'>
                                        <i className='fas fa-check'></i>
                                        <div className={''}>Changes to {successBannerTeamName} have been saved.</div>
                                    </div>}
                                    {sectionWarnings.length ? sectionWarnings.map((warning) => warning) : null}
                                    <PageHeader
                                        title={title}
                                        bottomBorder={true}
                                        hideBackButton={true}
                                        className={'company-form-header'}
                                        subtitle={subtitle}
                                        subtitleWithLink={subtitleWithLink}
                                    />
                                    {routes(featureFlagData)}
                                </div>
                            </div>
                            {confirmationModal && <ConfirmationModal 
                            isOpen={confirmationModal} 
                            messageTextProp={messageTextProp} 
                            title={confimationTitle}
                            actionButtonProp={actionButtonProp}
                            cancelButtonProp={cancelButtonProp}
                            actionFunc={this.confirmActionFunction}
                            cancelFunc={this.confirmCancelFunction}
                            />}
                        </Page>
                }
            </FeatureFlagContext.Consumer>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
        const {
        auth: { isAuthenticated, isVerbAdmin, isAccountOwner, loggedInUser, isInstructionalAdmin },
        companies: { byId },
        teams = {},
    } = state;
    const companyId = ownProps.match.params.id;
    const currentCompany = companyId ? byId[companyId] : {};
    const { teamsUnsavedFlag } = teams;
  
    return {
        isAuthenticated,
        currentCompany,
        isVerbAdmin,
        isAccountOwner,
        loggedInUser,
        isInstructionalAdmin,
        teamsUnsavedFlag,
    };
};

const mapDispatchToProps = (dispatch) => ({
    postCompany: (params) => dispatch(postCompany(params)),
    getCompany: (params) => dispatch(getCompany(params)),
    putCompany: (params) => dispatch(putCompany(params)),
    sendEmployeeNudges: (params) => dispatch(sendEmployeeNudges(params)),
    getManyTeamsAdmin: (params) => dispatch(getManyTeamsAdmin(params)),
    setUnsavedChanges: (params) => dispatch(setUnsavedChanges(params))
});

export const NewAddOrEditCompanyComponent = NewAddOrEditCompany;
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(NewAddOrEditCompany));
